import { EmojiEmotions } from '@mui/icons-material';
import { IconButton, Popper } from '@mui/material';
import EmojiPicker, { type EmojiClickData, EmojiStyle, SuggestionMode } from 'emoji-picker-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Constants for better maintainability
const EMOJI_PICKER_CONFIG = {
  width: 350,
  height: 450,
  zIndex: 1300,
  offset: [0, 8]
} as const;

interface MessageInputEmojiPickerProps {
  disabled?: boolean;
  onSelect?: (emoji: string) => void;
}

const MessageInputEmojiPicker: React.FC<MessageInputEmojiPickerProps> = ({ disabled, onSelect }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleEmojiClick = useCallback(
    (emojiData: EmojiClickData) => {
      onSelect?.(emojiData.emoji);
    },
    [onSelect]
  );

  const handleContainerInteraction = useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
  }, []);

  const previewConfig = {
    showPreview: true,
    defaultCaption: t('layout.chatInput.emojiPicker')
  };

  return (
    <>
      <IconButton disabled={disabled} onClick={handleToggle} size='small'>
        <EmojiEmotions />
      </IconButton>

      <Popper
        open={open}
        anchorEl={() => document.getElementById('message-input-card') as HTMLElement}
        placement='top-start'
        sx={{ zIndex: EMOJI_PICKER_CONFIG.zIndex }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: EMOJI_PICKER_CONFIG.offset
            }
          }
        ]}
      >
        <div onMouseLeave={handleClose} onClick={handleContainerInteraction} onKeyDown={handleContainerInteraction}>
          <EmojiPicker
            open
            skinTonesDisabled
            emojiStyle={EmojiStyle.APPLE}
            previewConfig={previewConfig}
            lazyLoadEmojis
            searchDisabled
            onEmojiClick={handleEmojiClick}
            suggestedEmojisMode={SuggestionMode.FREQUENT}
            width={EMOJI_PICKER_CONFIG.width}
            height={EMOJI_PICKER_CONFIG.height}
          />
        </div>
      </Popper>
    </>
  );
};

export default MessageInputEmojiPicker;
